import { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import App from './App';
import SetupWizard from './components/SetupWizard';
import ParentDashboard from './components/ParentDashboard';
import { getApiBaseUrl } from './constants';

interface UserProfile {
  id: string;
  clerk_id: string;
  email: string;
  name: string;
  role: string;
  grade: number | null;
  school: string | null;
  setup_complete: boolean;
}

function AuthenticatedApp() {
  const { getToken } = useAuth();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchProfile = async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${getApiBaseUrl()}/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch profile');
      const profile = await res.json();
      setUserProfile(profile);
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError('Failed to load profile. Please try refreshing.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#0a0a0f' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 40, height: 40, border: '3px solid rgba(124,58,237,0.2)', borderTopColor: '#7c3aed', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
          <p style={{ color: '#8b8b9e', fontFamily: 'Inter, sans-serif' }}>Loading...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', flexDirection: 'column', gap: '1rem', background: '#0a0a0f' }}>
        <p style={{ color: '#fca5a5', fontFamily: 'Inter, sans-serif' }}>{error}</p>
        <button onClick={() => { setError(''); setLoading(true); fetchProfile(); }} style={{ background: '#7c3aed', color: 'white', border: 'none', padding: '10px 24px', borderRadius: 8, cursor: 'pointer', fontFamily: 'Inter, sans-serif', fontWeight: 500 }}>Retry</button>
      </div>
    );
  }

  if (userProfile && !userProfile.setup_complete) {
    return <SetupWizard userProfile={userProfile} onComplete={fetchProfile} />;
  }

  // Route parents to parent dashboard
  if (userProfile && userProfile.role === 'parent') {
    return <ParentDashboard />;
  }

  return <App />;
}

export default AuthenticatedApp;
